// src/features/products/pages/BulkProductUploadErrorsPage.tsx
// REDESIGNED - Failed Products Dashboard with Premium UI/UX

import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ErrorState } from '@/components/ui/ErrorState';
import { downloadBulkUploadErrorReport } from '../services/productsApi';
import apiClient from '@/lib/api-client';
import {
    ArrowLeft, Download, FileSpreadsheet, Search,
    AlertTriangle, CheckCircle, XCircle, Info, X,
    ArrowUpDown, Upload, ChevronLeft, ChevronRight,
    ChevronsLeft, ChevronsRight, Package, RefreshCw,

    FileText, Rocket,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { clsx } from 'clsx';
import React from 'react';

// ============================================================
// TYPES
// ============================================================

interface UploadError {
    row: number;
    field: string;
    message: string;
    suggestedFix?: string;
}

interface UploadLog {
    id: string; tenantId: string; uploadedBy: string;
    fileName: string; totalRows: number; successCount: number;
    errorCount: number; errors: UploadError[]; status: string;
    createdAt: string; uploaderName?: string;
}

type Severity = 'critical' | 'warning' | 'info';

const ERRORS_PAGE_SIZE = 25;

// ============================================================
// HELPERS
// ============================================================

function getErrorSeverity(message: string): Severity {
    const lower = message.toLowerCase();
    if (lower.includes('required') || lower.includes('must be') || lower.includes('invalid') ||
        lower.includes('already exists') || lower.includes('not found') || lower.includes('duplicate')) {
        return 'critical';
    }
    if (lower.includes('should') || lower.includes('consider') || lower.includes('warning')) {
        return 'warning';
    }
    return 'info';
}

function getErrorCategory(message: string): string {
    const lower = message.toLowerCase();
    if (lower.includes('name') || lower.includes('slug')) return 'Product Info';
    if (lower.includes('price') || lower.includes('cost') || lower.includes('retail')) return 'Pricing';
    if (lower.includes('category') || lower.includes('cat')) return 'Category';
    if (lower.includes('sku')) return 'SKU';
    if (lower.includes('barcode')) return 'Barcode';
    if (lower.includes('uom') || lower.includes('unit')) return 'UOM';
    if (lower.includes('duplicate') || lower.includes('already exists')) return 'Duplicate';
    if (lower.includes('expiry') || lower.includes('shelf')) return 'Expiry';
    return 'Other';
}

function formatDateTime(date: string) {
    try {
        return new Date(date).toLocaleString('en-NG', {
            day: 'numeric', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    } catch { return 'N/A'; }
}

// ============================================================
// STAT CARD
// ============================================================

function StatCard({ label, value, icon: Icon, gradient, sublabel }: {
    label: string; value: number | string; icon: React.ElementType; gradient: string; sublabel?: string;
}) {
    return (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} whileHover={{ y: -3 }}
            className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 hover:shadow-md transition-all">
            <div className="flex items-start justify-between mb-3">
                <div className={`p-2.5 rounded-xl bg-gradient-to-br ${gradient} shadow-md`}>
                    <Icon className="w-5 h-5 text-white" />
                </div>
            </div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{label}</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{typeof value === 'number' ? value.toLocaleString() : value}</p>
            {sublabel && <p className="text-[10px] text-slate-400 mt-0.5">{sublabel}</p>}
        </motion.div>
    );
}

// ============================================================
// SEVERITY BADGE
// ============================================================

function SeverityBadge({ severity }: { severity: Severity }) {
    const config: Record<Severity, { style: string; icon: React.ElementType; label: string }> = {
        critical: { style: 'bg-rose-100 text-rose-700 border-rose-200', icon: XCircle, label: 'Critical' },
        warning: { style: 'bg-amber-100 text-amber-700 border-amber-200', icon: AlertTriangle, label: 'Warning' },
        info: { style: 'bg-blue-100 text-blue-700 border-blue-200', icon: Info, label: 'Info' },
    };
    const { style, icon: Icon, label } = config[severity];
    return (
        <span className={clsx('inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold border', style)}>
            <Icon className="w-3 h-3" />{label}
        </span>
    );
}

// ============================================================
// SKELETON
// ============================================================

function PageSkeleton() {
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-rose-50/20">
            <div className="max-w-7xl mx-auto p-6 space-y-5">
                <div className="rounded-3xl bg-slate-200 animate-pulse h-32" />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[...Array(4)].map((_, i) => (<div key={i} className="bg-white rounded-2xl border border-slate-200 p-5 animate-pulse h-28" />))}
                </div>
            </div>
        </div>
    );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export function BulkProductUploadErrorsPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const user = useAuthStore((s) => s.user);
    const addToast = useToastStore((s) => s.addToast);
    const uploadId = searchParams.get('uploadId');

    const [searchTerm, setSearchTerm] = useState('');
    const [severityFilter, setSeverityFilter] = useState<'all' | Severity>('all');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [sortField, setSortField] = useState<'row' | 'field' | 'message'>('row');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const [page, setPage] = useState(1);
    const [isDownloading, setIsDownloading] = useState(false);

    const { data: uploadLog, isLoading, isError, refetch } = useQuery<UploadLog | null>({
        queryKey: ['bulk-upload-log', uploadId],
        queryFn: async () => {
            if (!uploadId) return null;
            try {
                const { data } = await apiClient.get(`/tenants/${user!.tenantId}/products/bulk-upload/${uploadId}/log`);
                return data?.data || data || null;
            } catch { return null; }
        },
        enabled: !!user?.tenantId && !!uploadId,
    });

    const allErrors = uploadLog?.errors || [];

    // Filter + Sort
    const filteredErrors = useMemo(() => {
        let result = [...allErrors];
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            result = result.filter(e =>
                e.field.toLowerCase().includes(term) ||
                e.message.toLowerCase().includes(term) ||
                String(e.row).includes(term) ||
                (e.suggestedFix && e.suggestedFix.toLowerCase().includes(term))
            );
        }
        if (severityFilter !== 'all') {
            result = result.filter(e => getErrorSeverity(e.message) === severityFilter);
        }
        if (categoryFilter !== 'all') {
            result = result.filter(e => getErrorCategory(e.message) === categoryFilter);
        }
        result.sort((a, b) => {
            let comparison = 0;
            if (sortField === 'row') comparison = a.row - b.row;
            else if (sortField === 'field') comparison = a.field.localeCompare(b.field);
            else comparison = a.message.localeCompare(b.message);
            return sortDirection === 'asc' ? comparison : -comparison;
        });
        return result;
    }, [allErrors, searchTerm, severityFilter, categoryFilter, sortField, sortDirection]);

    useEffect(() => { setPage(1); }, [searchTerm, severityFilter, categoryFilter]);

    // Pagination
    const totalPages = Math.ceil(filteredErrors.length / ERRORS_PAGE_SIZE);
    const paginatedErrors = filteredErrors.slice((page - 1) * ERRORS_PAGE_SIZE, page * ERRORS_PAGE_SIZE);
    const startItem = filteredErrors.length > 0 ? (page - 1) * ERRORS_PAGE_SIZE + 1 : 0;
    const endItem = Math.min(page * ERRORS_PAGE_SIZE, filteredErrors.length);

    const pageNumbers: (number | 'ellipsis')[] = [];
    if (totalPages <= 7) {
        for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
    } else {
        pageNumbers.push(1);
        if (page > 3) pageNumbers.push('ellipsis');
        for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) pageNumbers.push(i);
        if (page < totalPages - 2) pageNumbers.push('ellipsis');
        pageNumbers.push(totalPages);
    }

    const toggleSort = (field: 'row' | 'field' | 'message') => {
        if (sortField === field) setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        else { setSortField(field); setSortDirection('asc'); }
    };

    const handleDownloadReport = async () => {
        if (!uploadId) return;
        setIsDownloading(true);
        try {
            const blob = await downloadBulkUploadErrorReport(user!.tenantId, uploadId);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = `nyan-product-errors-${uploadId}.xlsx`; a.click();
            URL.revokeObjectURL(url);
            addToast({ type: 'success', title: 'Report Downloaded 📥', message: 'Error report has been downloaded.' });
        } catch {
            addToast({ type: 'error', title: 'Download Failed', message: 'Could not download the error report.' });
        } finally { setIsDownloading(false); }
    };

    const criticalCount = allErrors.filter(e => getErrorSeverity(e.message) === 'critical').length;
    const warningCount = allErrors.filter(e => getErrorSeverity(e.message) === 'warning').length;
    const infoCount = allErrors.filter(e => getErrorSeverity(e.message) === 'info').length;

    const categories = useMemo(() => {
        const cats = new Set<string>();
        allErrors.forEach(e => cats.add(getErrorCategory(e.message)));
        return Array.from(cats).sort();
    }, [allErrors]);

    const successRate = uploadLog && uploadLog.totalRows > 0
        ? ((uploadLog.successCount / uploadLog.totalRows) * 100).toFixed(1)
        : '0';

    // ============================================================
    // LOADING / ERROR
    // ============================================================
    if (isLoading) return <PageSkeleton />;
    if (isError || !uploadLog) return (
        <div className="min-h-screen flex items-center justify-center p-4">
            <ErrorState title="Upload log not found" message="This upload report may have been deleted." onRetry={() => refetch()} onBack={() => navigate('/products')} />
        </div>
    );

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-rose-50/20">
            <div className="max-w-7xl mx-auto p-4 lg:p-6 space-y-5">

                {/* Back Button */}
                <motion.button initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
                    onClick={() => navigate('/products/bulk-upload')}
                    className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-rose-600 group transition-colors">
                    <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
                    Back to Bulk Upload
                </motion.button>

                {/* ========== HERO HEADER ========== */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                    className="relative overflow-hidden rounded-3xl shadow-2xl bg-gradient-to-br from-slate-700 via-slate-800 to-rose-900">
                    <div className="absolute inset-0 opacity-10">
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.15),transparent_50%)]" />
                        <div className="absolute top-0 right-0 w-96 h-96 bg-rose-400/20 rounded-full blur-3xl" />
                    </div>
                    <div className="relative px-6 lg:px-10 py-8">
                        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
                            <div className="flex items-center gap-4">
                                <motion.div whileHover={{ rotate: -5, scale: 1.05 }}
                                    className="w-16 h-16 rounded-2xl bg-rose-400/20 backdrop-blur-sm ring-1 ring-rose-400/30 flex items-center justify-center shadow-xl">
                                    <AlertTriangle size={32} className="text-rose-300" />
                                </motion.div>
                                <div>
                                    <h1 className="text-3xl lg:text-4xl font-black text-white tracking-tight">Failed Products</h1>
                                    <p className="text-slate-300 text-sm mt-1 font-medium">
                                        {uploadLog.successCount} succeeded · <span className="text-rose-300 font-bold">{uploadLog.errorCount} failed</span> · {uploadLog.fileName || 'Bulk Upload'}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <Button size="sm" className="!bg-white/10 !text-white !border-white/20 hover:!bg-white/20 !font-medium"
                                    leftIcon={<RefreshCw size={14} />} onClick={() => refetch()}>Refresh</Button>
                                <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                                    <Button size="md" className="!bg-white !text-slate-800 hover:!bg-slate-100 !font-bold !px-6 !py-3 !rounded-2xl !text-base !border-2 !border-white/80 !shadow-xl !shadow-black/20"
                                        leftIcon={<Upload size={18} />} onClick={() => navigate('/products/bulk-upload')}>
                                        Re-upload Fixed File
                                    </Button>
                                </motion.div>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* ========== CONTEXT BANNER ========== */}
                {uploadLog.errorCount > 0 && (
                    <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
                        className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-5 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-amber-100 rounded-xl">
                                <AlertTriangle size={20} className="text-amber-600" />
                            </div>
                            <div>
                                <p className="text-sm font-bold text-amber-800">
                                    {uploadLog.successCount} of {uploadLog.totalRows} products imported successfully
                                </p>
                                <p className="text-xs text-amber-600 mt-0.5">
                                    Fix the {uploadLog.errorCount} errors below and re-upload the corrected file
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button onClick={() => navigate('/products')}
                                className="px-4 py-2.5 bg-white border border-amber-300 text-amber-700 rounded-xl text-sm font-semibold hover:bg-amber-50 transition-colors">
                                Skip & Go to Products
                            </button>
                        </div>
                    </motion.div>
                )}

                {/* ========== STATS ========== */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <StatCard label="Total Rows" value={uploadLog.totalRows} icon={FileSpreadsheet} gradient="from-slate-500 to-slate-700" />
                    <StatCard label="Success" value={uploadLog.successCount} icon={CheckCircle} gradient="from-emerald-500 to-teal-600" sublabel={`${successRate}% success rate`} />
                    <StatCard label="Failed" value={uploadLog.errorCount} icon={XCircle} gradient="from-rose-500 to-pink-600" />
                    <StatCard label="To Fix" value={criticalCount + warningCount} icon={AlertTriangle} gradient="from-amber-500 to-orange-600" sublabel={`${criticalCount} critical`} />
                </div>

                {/* ========== FILTERS ========== */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm sticky top-4 z-30 backdrop-blur-xl bg-white/95 p-3">
                    <div className="flex flex-col sm:flex-row gap-3">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <input type="text" placeholder="Search errors..." value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-10 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-rose-500/20 focus:border-rose-400 focus:bg-white transition-all" />
                            {searchTerm && (
                                <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600">
                                    <X size={14} />
                                </button>
                            )}
                        </div>
                        <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1">
                            {(['all', 'critical', 'warning', 'info'] as const).map(key => (
                                <button key={key} onClick={() => setSeverityFilter(key)}
                                    className={clsx('px-3 py-1.5 rounded-lg text-xs font-medium transition-all',
                                        severityFilter === key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700')}>
                                    {key === 'all' ? `All (${allErrors.length})` : `${key.charAt(0).toUpperCase() + key.slice(1)} (${key === 'critical' ? criticalCount : key === 'warning' ? warningCount : infoCount})`}
                                </button>
                            ))}
                        </div>
                        <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
                            className="px-3 py-2 border border-slate-200 rounded-xl text-xs bg-white">
                            <option value="all">All Categories</option>
                            {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                        </select>
                        {(searchTerm || severityFilter !== 'all' || categoryFilter !== 'all') && (
                            <button onClick={() => { setSearchTerm(''); setSeverityFilter('all'); setCategoryFilter('all'); }}
                                className="text-xs text-rose-500 hover:text-rose-600 font-medium px-2 whitespace-nowrap">Clear filters</button>
                        )}
                    </div>
                </div>

                {/* ========== ERROR TABLE ========== */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                    <div className="px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white flex items-center justify-between">
                        <div>
                            <h3 className="font-bold text-slate-800 flex items-center gap-2">
                                <FileText size={16} className="text-rose-500" /> Error Details
                            </h3>
                            <p className="text-xs text-slate-500">
                                Showing {startItem}-{endItem} of {filteredErrors.length} errors
                                {filteredErrors.length !== allErrors.length && ` (filtered from ${allErrors.length})`}
                            </p>
                        </div>
                        <button onClick={handleDownloadReport} disabled={isDownloading}
                            className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-xl text-xs font-medium hover:bg-slate-50 transition-colors disabled:opacity-50">
                            <Download size={14} /> {isDownloading ? 'Downloading...' : 'Download Excel'}
                        </button>
                    </div>

                    {filteredErrors.length === 0 ? (
                        <div className="text-center py-16">
                            <Search className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                            <p className="text-sm font-medium text-slate-500">No matching errors</p>
                            <button onClick={() => { setSearchTerm(''); setSeverityFilter('all'); setCategoryFilter('all'); }}
                                className="text-xs text-emerald-600 font-medium mt-1">Clear all filters</button>
                        </div>
                    ) : (
                        <>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-slate-100">
                                    <thead>
                                        <tr className="bg-slate-50">
                                            <th className="px-6 py-3 text-left cursor-pointer hover:text-slate-700 w-16" onClick={() => toggleSort('row')}>
                                                <div className="flex items-center gap-1 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                                                    Row #{sortField === 'row' && <ArrowUpDown className="w-3 h-3" />}
                                                </div>
                                            </th>
                                            <th className="px-6 py-3 text-left cursor-pointer hover:text-slate-700" onClick={() => toggleSort('field')}>
                                                <div className="flex items-center gap-1 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                                                    Field{sortField === 'field' && <ArrowUpDown className="w-3 h-3" />}
                                                </div>
                                            </th>
                                            <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider w-20">Severity</th>
                                            <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider w-24">Category</th>
                                            <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider">Error Message</th>
                                            <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider">How to Fix</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {paginatedErrors.map((error, idx) => {
                                            const severity = getErrorSeverity(error.message);
                                            return (
                                                <motion.tr key={`${error.row}-${idx}`}
                                                    initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                                                    transition={{ delay: Math.min(idx * 0.01, 0.3) }}
                                                    className={clsx('transition-colors group',
                                                        severity === 'critical' ? 'bg-rose-50/30 hover:bg-rose-50' :
                                                            severity === 'warning' ? 'bg-amber-50/20 hover:bg-amber-50' : 'hover:bg-slate-50')}>
                                                    <td className="px-6 py-4">
                                                        <span className="text-sm font-mono font-bold text-slate-600">#{error.row}</span>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <span className="text-sm font-medium text-slate-700">{error.field}</span>
                                                    </td>
                                                    <td className="px-6 py-4"><SeverityBadge severity={severity} /></td>
                                                    <td className="px-6 py-4"><Badge variant="neutral" size="sm">{getErrorCategory(error.message)}</Badge></td>
                                                    <td className="px-6 py-4">
                                                        <p className={clsx('text-sm max-w-lg',
                                                            severity === 'critical' ? 'text-rose-700 font-medium' :
                                                                severity === 'warning' ? 'text-amber-700' : 'text-slate-600')}>
                                                            {error.message}
                                                        </p>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        {error.suggestedFix ? (
                                                            <span className="text-sm text-emerald-600 font-medium">{error.suggestedFix}</span>
                                                        ) : (
                                                            <span className="text-sm text-slate-400 italic">—</span>
                                                        )}
                                                    </td>
                                                </motion.tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="px-6 py-4 bg-slate-50/50 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3">
                                    <p className="text-sm text-slate-600">
                                        Showing <span className="font-semibold text-slate-900">{startItem}</span> to{' '}
                                        <span className="font-semibold text-slate-900">{endItem}</span> of{' '}
                                        <span className="font-semibold text-slate-900">{filteredErrors.length}</span> errors
                                    </p>
                                    <div className="flex gap-2 flex-wrap">
                                        <button onClick={() => setPage(1)} disabled={page === 1} className="p-2 rounded-lg text-slate-400 hover:text-slate-600 disabled:opacity-30" type="button"><ChevronsLeft className="w-4 h-4" /></button>
                                        <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-4 py-2 border border-slate-200 rounded-xl text-sm disabled:opacity-40 hover:bg-slate-100 font-medium transition flex items-center gap-1" type="button"><ChevronLeft size={14} /> Prev</button>
                                        {pageNumbers.map((p, idx) => (
                                            <React.Fragment key={idx}>
                                                {p === 'ellipsis' ? <span className="px-2 py-2 text-slate-400">...</span> : (
                                                    <button onClick={() => setPage(p as number)}
                                                        className={clsx('px-4 py-2 rounded-xl text-sm font-medium transition',
                                                            page === p ? 'bg-gradient-to-r from-rose-600 to-rose-700 text-white shadow-md shadow-rose-500/25' : 'border border-slate-200 hover:bg-slate-100 text-slate-700')}
                                                        type="button">{p}</button>
                                                )}
                                            </React.Fragment>
                                        ))}
                                        <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="px-4 py-2 border border-slate-200 rounded-xl text-sm disabled:opacity-40 hover:bg-slate-100 font-medium transition flex items-center gap-1" type="button">Next <ChevronRight size={14} /></button>
                                        <button onClick={() => setPage(totalPages)} disabled={page >= totalPages} className="p-2 rounded-lg text-slate-400 hover:text-slate-600 disabled:opacity-30" type="button"><ChevronsRight className="w-4 h-4" /></button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* ========== BOTTOM ACTIONS ========== */}
                <div className="flex justify-center gap-3 pb-8">
                    <button onClick={() => navigate('/products/bulk-upload')}
                        className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-rose-600 to-rose-700 text-white rounded-xl font-semibold text-sm shadow-lg shadow-rose-500/25 hover:from-rose-700 hover:to-rose-800 transition-all">
                        <Rocket size={16} /> Re-upload Fixed File
                    </button>
                    <button onClick={() => navigate('/products')}
                        className="flex items-center gap-2 px-6 py-3 bg-white border-2 border-slate-200 rounded-xl font-semibold text-sm text-slate-700 hover:bg-slate-50 transition-all shadow-sm">
                        <Package size={16} /> Go to Products
                    </button>
                </div>
            </div>
        </div>
    );
}